import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import apiClient from '../api';
import Breadcrumb from '../components/Breadcrumb';
import PerformanceChart from '../components/PerformanceChart';
import { getPlayerPerformance } from '../api/stats';
import 'font-awesome/css/font-awesome.min.css';
import './PlayerProfile.css';

function PlayerProfile() {
    const { id } = useParams();

    const [userData, setUserData] = useState(null);
    const [performance, setPerformance] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const formatName = (str) => {
        if (!str) return '';
        const trimmed = str.trim();
        return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
    };

    const getInitials = (first, last) => {
        const f = first ? first[0] : '';
        const l = last ? last[0] : '';
        return (f + l).toUpperCase() || '?';
    };

    useEffect(() => {
        const fetchPlayer = async () => {
            setLoading(true);
            setError(null);
            try {
                const [{ data }, perfData] = await Promise.all([
                    apiClient.get(`/players/${id}/withStats`),
                    getPlayerPerformance(id).catch(() => []),
                ]);

                const playerInfo = data.player || {};
                const statsInfo = data.stats || {};

                setUserData({
                    player: playerInfo,
                    stats: statsInfo,
                    matchDays: data.matchDays,
                    firstName: formatName(playerInfo.name),
                    lastName: formatName(playerInfo.lastName),
                    email: playerInfo.email,
                    teamName: playerInfo.teamName
                });
                setPerformance(perfData || []);
            } catch (err) {
                console.error(err);
                setError("No se pudo cargar el perfil de la jugadora.");
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchPlayer();
        }
    }, [id]);

    if (loading) return (
        <div className="player-profile-wrapper">
            <div className="loading-container">
                <div className="spinner"></div>
                <p>Cargando ficha...</p>
            </div>
        </div>
    );

    if (error || !userData) return (
        <div className="player-profile-wrapper">
            <div className="error-banner">
                <i className="fa fa-exclamation-triangle"></i> {error || "Jugadora no encontrada"}
            </div>
        </div>
    );

    const fullName = [userData.firstName, userData.lastName].filter(Boolean).join(' ');

    return (
        <div className="player-profile-wrapper">
            <div className="profile-hero">
                <div className="profile-hero-top">
                    <Breadcrumb crumbs={[
                        { label: 'Inicio', to: '/dashboard' },
                        { label: fullName || 'Jugadora' },
                    ]} />
                </div>
                <div className="hero-content">
                    <span className="hero-badge">Ficha Técnica</span>
                    <h1 className="hero-title">{fullName}</h1>
                    <p className="hero-subtitle">
                        Perfil de jugadora y estadísticas de la temporada actual.
                    </p>
                </div>
            </div>

            <div className="profile-content-grid">

                <div className="info-card">
                    <div className="card-header">
                        <h2>Información</h2>
                        <p>Datos generales</p>
                    </div>

                    <div className="profile-main-info">
                        <div className="avatar-circle">
                            {getInitials(userData.firstName, userData.lastName)}
                        </div>
                        <div className="profile-names">
                            <h3>{fullName}</h3>
                            <span>Jugadora</span>
                        </div>
                    </div>

                    <div className="details-list">
                        <div className="detail-row">
                            <span className="detail-label">Equipo</span>
                            <span className="detail-value">{userData.teamName || 'Sin asignar'}</span>
                        </div>
                        <div className="detail-row">
                            <span className="detail-label">Email</span>
                            <span className="detail-value">{userData.email}</span>
                        </div>
                        <div className="detail-row">
                            <span className="detail-label">Posición</span>
                            <span className="detail-value">{userData.player.position || '-'}</span>
                        </div>
                    </div>
                </div>

                <div className="info-card">
                    <div className="card-header" style={{display:'flex', justifyContent:'space-between'}}>
                        <div>
                            <h2>Rendimiento</h2>
                            <p>Estadísticas acumuladas</p>
                        </div>
                        <i className="fa fa-bar-chart" style={{fontSize:'1.5rem', color:'var(--brand-primary)', opacity:0.5}}></i>
                    </div>

                    <div className="stats-grid">
                        <div className="stat-box">
                            <span className="stat-value">{userData.stats.gamesPlayed || 0}</span>
                            <span className="stat-label">Partidos</span>
                        </div>
                        <div className="stat-box">
                            <span className="stat-value">{userData.stats.points || 0}</span>
                            <span className="stat-label">Puntos</span>
                        </div>
                        <div className="stat-box">
                            <span className="stat-value">{userData.stats.wins || 0}</span>
                            <span className="stat-label">Victorias</span>
                        </div>
                        <div className="stat-box">
                            <span className="stat-value">{userData.stats.losses || 0}</span>
                            <span className="stat-label">Derrotas</span>
                        </div>
                        <div className="stat-box">
                            <span className="stat-value">{userData.stats.availability || 0}</span>
                            <span className="stat-label">Disponibilidad</span>
                        </div>
                    </div>
                </div>

                <div className="info-card">
                    <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <div>
                            <h2>Últimos partidos</h2>
                            <p>Resultados recientes</p>
                        </div>
                        <i className="fa fa-history" style={{ fontSize: '1.5rem', color: 'var(--brand-primary)', opacity: 0.5 }}></i>
                    </div>
                    {performance.length === 0 ? (
                        <p className="no-matches-msg">Sin partidos registrados todavía.</p>
                    ) : (
                        <div className="recent-matches-list">
                            {[...performance].reverse().slice(0, 5).map((p, i) => (
                                <div key={i} className={`recent-match-row ${p.won ? 'recent-match--win' : 'recent-match--loss'}`}>
                                    <span className={`recent-match-badge ${p.won ? 'badge--win' : 'badge--loss'}`}>
                                        {p.won ? 'V' : 'D'}
                                    </span>
                                    <span className="recent-match-opponent">vs {p.opponentName || '—'}</span>
                                    <span className="recent-match-date">
                                        {new Date(p.date).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="info-card">
                    <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <div>
                            <h2>Evolución</h2>
                            <p>Resultado acumulado partido a partido</p>
                        </div>
                        <i className="fa fa-line-chart" style={{ fontSize: '1.5rem', color: 'var(--brand-primary)', opacity: 0.5 }}></i>
                    </div>
                    <PerformanceChart data={performance} />
                </div>

            </div>
        </div>
    );
}

export default PlayerProfile;